interface UseTriggerProps {
	ref: HTMLElement | null;
	documentPercent?: number;
	documentEndPercent?: number;
	marker?: boolean;
	once?: boolean;
	onEnter?: (entry: IntersectionObserverEntry) => void;
	onBack?: (entry: IntersectionObserverEntry) => void;
}

const useTrigger = ({ ref, documentPercent = 20, documentEndPercent = 0, marker = false, onEnter, onBack, once }: UseTriggerProps) => {
	if (!ref) return;

	const documentMarker = window.innerHeight * (documentPercent / 100);
	const documentMarkerEnd = window.innerHeight * (documentEndPercent / 100);
	let documentLineStart: HTMLDivElement | null = null;
	let documentLineEnd: HTMLDivElement | null = null;
	let elementLineStart: HTMLDivElement | null = null;
	let elementLineEnd: HTMLDivElement | null = null;

	let textEnd = document.createElement('span');
	let textStart = document.createElement('span');
	textStart.innerHTML = 'Start';
	textEnd.innerHTML = 'End';

	if (marker) {
		documentLineStart = document.createElement('div');
		Object.assign(documentLineStart.style, {
			position: 'fixed',
			zIndex: '100000000',
			left: '0',
			width: '100%',
			height: '2px',
			backgroundColor: 'green',
			top: `${documentMarker}px`,
		});

		document.body.appendChild(documentLineStart);

		documentLineEnd = document.createElement('div');
		Object.assign(documentLineEnd.style, {
			position: 'fixed',
			zIndex: '100000000',
			left: '0',
			width: '100%',
			height: '2px',
			backgroundColor: 'red',
			top: `${documentMarkerEnd}px`,
		});
		document.body.appendChild(documentLineEnd);

		elementLineStart = document.createElement('div');
		elementLineStart.appendChild(textStart);
		Object.assign(elementLineStart.style, {
			position: 'absolute',
			zIndex: '100000000',
			left: '0',
			width: '10%',
			height: '2px',
			backgroundColor: 'green',
			top: `${ref.getBoundingClientRect().top + window.scrollY}px`,
		});
		document.body.appendChild(elementLineStart);

		elementLineEnd = document.createElement('div');
		elementLineEnd.appendChild(textEnd);
		Object.assign(elementLineEnd.style, {
			position: 'absolute',
			zIndex: '100000000',
			left: '0',
			width: '10%',
			height: '2px',
			backgroundColor: 'red',
			top: `${ref.getBoundingClientRect().top + window.scrollY + ref.clientHeight}px`,
		});
		document.body.appendChild(elementLineEnd);
	}
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					onEnter?.(entry);
					if (once) observer.disconnect();
				} else {
					onBack?.(entry);
				}
			});
		},
		{
			root: null,
			rootMargin: `-${documentEndPercent}% 0px -${100 - documentPercent}% 0px`,
			//! for test threshold
			// threshold: Array.from({ length: 101 }, (_, i) => i / 100),
		}
	);

	observer.observe(ref);

	if (documentMarker > ref.getBoundingClientRect().top) {
		onEnter?.({
			boundingClientRect: ref.getBoundingClientRect(),
			intersectionRatio: 1,
			intersectionRect: ref.getBoundingClientRect(),
			isIntersecting: true,
			rootBounds: null,
			target: ref,
			time: performance.now(),
		} as IntersectionObserverEntry);
	}

	return () => {
		observer.disconnect();
		if (marker && documentLineStart) {
			document.body.removeChild(documentLineStart);
			document.body.removeChild(documentLineEnd);
			document.body.removeChild(elementLineStart);
			document.body.removeChild(elementLineEnd);
		}
	};
};

export default useTrigger;
